import { useEffect, useState } from 'react';
import Button from 'src/components/ui/Button';
import { ConfettiIcon } from '@phosphor-icons/react';
import { useRightPanelControl } from 'src/hooks/useRightPanelControl';
import OnboardingContent from './OnboardingContent';
import Step from 'src/components/ui/Step';
import useTenantNavigate from 'src/hooks/useTenantNavigate';
import useActiveTenantRole from 'src/hooks/useActiveTenantRole';
import RightPanelShell from 'src/components/layouts/RightPanelShell';

export default function ChatGuide() {
  const { hideOnboarding, setContent } = useRightPanelControl();
  const navigate = useTenantNavigate();
  const { isStandardMember: isPlatformUser } = useActiveTenantRole();

  const isMemoryEnabled = isPlatformUser;
  const isSpecialistsEnabled = isPlatformUser;

  const [steps, setSteps] = useState<{
    tour: 'incomplete' | 'loading' | 'complete';
    question: 'incomplete' | 'loading' | 'complete';
    memory: 'incomplete' | 'loading' | 'complete';
    specialist: 'incomplete' | 'loading' | 'complete';
    draft: 'incomplete' | 'loading' | 'complete';
    file: 'incomplete' | 'loading' | 'complete';
  }>({
    tour: 'loading',
    question: 'incomplete',
    memory: 'incomplete',
    specialist: 'incomplete',
    draft: 'incomplete',
    file: 'incomplete'
  });

  // Hydrate from localStorage on mount so completion persists across navigations
  useEffect(() => {
    try {
      const tourDone = localStorage.getItem('innex-chat-tour-completed') === 'true';
      const asked = localStorage.getItem('innex-chat-question-asked') === 'true';
      const memoryEnabled = localStorage.getItem('innex-chat-memory-enabled') === 'true';
      const specialistUsed = localStorage.getItem('innex-chat-specialist-used') === 'true';
      const drafted = localStorage.getItem('innex-chat-draft-created') === 'true';
      const summarised = localStorage.getItem('innex-chat-file-summarised') === 'true';

      setSteps({
        tour: tourDone ? 'complete' : 'loading',
        question: asked ? 'complete' : tourDone ? 'loading' : 'incomplete',
        memory: isMemoryEnabled ? (memoryEnabled ? 'complete' : asked ? 'loading' : 'incomplete') : 'complete',
        draft: drafted ? 'complete' : (isMemoryEnabled ? memoryEnabled : asked) ? 'loading' : 'incomplete',
        file: summarised ? 'complete' : drafted ? 'loading' : 'incomplete',
        specialist: isSpecialistsEnabled
          ? specialistUsed
            ? 'complete'
            : summarised
              ? 'loading'
              : 'incomplete'
          : 'complete'
      });
    } catch {}
  }, [isMemoryEnabled, isSpecialistsEnabled]);

  // Helper to set an active step: clicked becomes loading, any current loading becomes incomplete
  const setActiveStep = (key: 'tour' | 'question' | 'memory' | 'specialist' | 'draft' | 'file') => {
    setSteps((prev) => {
      if (prev[key] === 'loading') return prev;
      const next = { ...prev } as typeof prev;
      if (next.tour === 'loading') next.tour = 'incomplete';
      if (next.question === 'loading') next.question = 'incomplete';
      if (next.memory === 'loading') next.memory = 'incomplete';
      if (next.specialist === 'loading') next.specialist = 'incomplete';
      if (next.draft === 'loading') next.draft = 'incomplete';
      if (next.file === 'loading') next.file = 'incomplete';
      next[key] = 'loading';
      return next;
    });
  };

  // Ensure underlying page is /chat
  useEffect(() => {
    if (!window.location.pathname.includes('/chat')) {
      navigate('/chat');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tour completed auto-advance
  useEffect(() => {
    const completed = localStorage.getItem('innex-chat-tour-completed') === 'true';
    if (completed) {
      setSteps((s) => ({ ...s, tour: 'complete', question: 'loading' }));
    }
  }, []);

  useEffect(() => {
    if (steps.tour !== 'loading') return;
    const id = setInterval(() => {
      const done = localStorage.getItem('innex-chat-tour-completed') === 'true';
      if (done) {
        setSteps((s) => ({
          ...s,
          tour: 'complete',
          question: s.question === 'incomplete' ? 'loading' : s.question
        }));
        clearInterval(id);
      }
    }, 500);
    return () => clearInterval(id);
  }, [steps.tour]);

  const backToOnboarding = () => {
    setContent(<OnboardingContent onComplete={hideOnboarding} />);
  };

  const header = (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        className="text-sm text-secondary-blue hover:underline self-start"
        onClick={backToOnboarding}
      >
        ← Back to all modules
      </button>
      <h2 className="text-lg font-semibold text-content-default">Chat Assistant</h2>
    </div>
  );

  const allComplete =
    steps.tour === 'complete' &&
    steps.question === 'complete' &&
    steps.memory === 'complete' &&
    steps.specialist === 'complete' &&
    steps.draft === 'complete' &&
    steps.file === 'complete';

  const skipStep = () => {
    setSteps((s) => {
      const order = ['tour', 'question', 'draft', 'file', 'specialist'] as const;
      const loadingIdx = order.findIndex((k) => s[k] === 'loading');
      const nextState = { ...s } as typeof s;
      if (loadingIdx >= 0) {
        nextState[order[loadingIdx]] = 'incomplete';
        const relNextIdx = order.slice(loadingIdx + 1).findIndex((k) => s[k] === 'incomplete');
        if (relNextIdx >= 0) {
          const idx = loadingIdx + 1 + relNextIdx;
          nextState[order[idx]] = 'loading';
          return nextState;
        }
        const firstIncompleteIdx = order.findIndex((k) => s[k] === 'incomplete');
        if (firstIncompleteIdx >= 0) {
          nextState[order[firstIncompleteIdx]] = 'loading';
          return nextState;
        }
        return s;
      }
      const firstIncompleteIdx = order.findIndex((k) => s[k] === 'incomplete');
      if (firstIncompleteIdx >= 0) {
        nextState[order[firstIncompleteIdx]] = 'loading';
        return nextState;
      }
      return s;
    });
  };

  const footer = allComplete ? (
    <Button
      variant="primary"
      size="md"
      className="w-full"
      onClick={() => {
        try {
          localStorage.setItem('innex-onboarding-module-chat-completed', 'true');
        } catch {}
        hideOnboarding();
      }}
    >
      Mark as complete
    </Button>
  ) : (
    <Button variant="outline" size="md" className="w-full" onClick={skipStep}>
      Skip Step
    </Button>
  );

  return (
    <RightPanelShell header={header} footer={footer}>
      <div className="flex flex-col gap-2">
        {(() => {
          return (
            <>
              <Step
                title="Complete Tour"
                state={steps.tour}
                onTitleClick={() => {
                  try {
                    localStorage.removeItem('innex-chat-tour-completed');
                  } catch {}
                  setActiveStep('tour');
                }}
              >
                <div className="flex flex-col gap-2">
                  <div className="text-sm text-content-subtle">Start our short guided tour of the Chat Assistant.</div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      try {
                        window.dispatchEvent(new Event('start-chat-tour'));
                      } catch {}
                    }}
                  >
                    Start tour
                  </Button>
                </div>
              </Step>

              <Step title="Ask any question" state={steps.question} onTitleClick={() => setActiveStep('question')}>
                {steps.question === 'loading' ? (
                  <div className="flex flex-col gap-2">
                    <div className="text-sm text-content-subtle">
                      Ask any question you have. If you can't think of one, try:
                    </div>
                    <div className="text-sm italic">
                      What are the key requirements of the Building Safety Act for hospitals?
                    </div>
                    <div className="text-sm text-content-subtle">
                      After you get a response, please press the "I asked a question" button.
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        try {
                          localStorage.setItem('innex-chat-question-asked', 'true');
                        } catch {}
                        if (isMemoryEnabled) {
                          setSteps((s) => ({ ...s, question: 'complete', memory: 'loading' }));
                        } else {
                          setSteps((s) => ({ ...s, question: 'complete', draft: 'loading' }));
                        }
                      }}
                    >
                      I asked a question
                    </Button>
                  </div>
                ) : undefined}
              </Step>

              {isMemoryEnabled && (
                <Step title="Turn on Memory" state={steps.memory} onTitleClick={() => setActiveStep('memory')}>
                  {steps.memory === 'loading' ? (
                    <div className="flex flex-col gap-2">
                      <div className="text-sm text-content-subtle">
                        Enable Memory if you want CompliMind to remember your preferences and context across
                        conversations. Click the Memory button in the top right to turn it on.
                      </div>
                      <div className="text-sm text-content-subtle">
                        If you don't want to enable memory, click the button below to continue.
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => {
                            try {
                              localStorage.setItem('innex-chat-memory-enabled', 'true');
                            } catch {}
                            setSteps((s) => ({ ...s, memory: 'complete', draft: 'loading' }));
                          }}
                        >
                          I enabled memory
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            try {
                              localStorage.setItem('innex-chat-memory-enabled', 'true');
                            } catch {}
                            setSteps((s) => ({ ...s, memory: 'complete', draft: 'loading' }));
                          }}
                        >
                          Skip for now
                        </Button>
                      </div>
                    </div>
                  ) : undefined}
                </Step>
              )}

              <Step title="Draft an email or document" state={steps.draft} onTitleClick={() => setActiveStep('draft')}>
                {steps.draft === 'loading' ? (
                  <div className="flex flex-col gap-2">
                    <div className="text-sm text-content-subtle">Ask the following to draft a short email:</div>
                    <div className="text-sm italic">
                      Help me draft an email to the ward manager summarising last week's incident review outcomes and
                      next steps.
                    </div>
                    <div className="text-sm text-content-subtle">
                      After you get a response, please press the "I drafted an email/document" button.
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        try {
                          localStorage.setItem('innex-chat-draft-created', 'true');
                        } catch {}
                        setSteps((s) => ({ ...s, draft: 'complete', file: 'loading' }));
                      }}
                    >
                      I drafted an email/document
                    </Button>
                  </div>
                ) : undefined}
              </Step>

              <Step title="Add a File and Summarise" state={steps.file} onTitleClick={() => setActiveStep('file')}>
                {steps.file === 'loading' ? (
                  <div className="flex flex-col gap-2">
                    <div className="text-sm text-content-subtle">
                      1. Download the sample file. <br /> 2. In Chat, upload the file. <br /> 3. Ask:{' '}
                      <i>"Summarise this document in 5 bullet points"</i>.
                      <div className="text-sm text-content-subtle">
                        After you get a response, please press the "I uploaded and asked for a summary" button.
                      </div>
                    </div>
                    <Button
                      onClick={async () => {
                        try {
                          const urlStr =
                            'https://innexpublic.blob.core.windows.net/platform-assets/onboarding/Waste Policy.pdf';
                          const response = await fetch(urlStr);
                          if (!response.ok) throw new Error('Failed to fetch');
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = 'Waste Policy.pdf';
                          link.style.display = 'none';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(url);
                        } catch (error) {
                          console.error('Download failed, opening in new tab:', error);
                          window.open(
                            'https://innexpublic.blob.core.windows.net/platform-assets/onboarding/Waste Policy.pdf',
                            '_blank'
                          );
                        }
                      }}
                      variant="outline"
                      size="sm"
                    >
                      Download sample file
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        try {
                          localStorage.setItem('innex-chat-file-summarised', 'true');
                        } catch {}
                        setSteps((s) => ({ ...s, file: 'complete', specialist: 'loading' }));
                      }}
                    >
                      I uploaded and asked for a summary
                    </Button>
                  </div>
                ) : undefined}
              </Step>

              {isSpecialistsEnabled && (
                <Step
                  title="Use a Specialist"
                  state={steps.specialist}
                  isLast={!allComplete}
                  onTitleClick={() => setActiveStep('specialist')}
                >
                  {steps.specialist === 'loading' ? (
                    <div className="flex flex-col gap-2">
                      <div className="text-sm text-content-subtle">
                        Specialists are pre-configured assistants tailored for specific tasks or domains.
                      </div>
                      <div className="text-sm text-content-subtle">
                        1. Click the <strong>+</strong> (Add Specialist) button in the chat search bar.
                        <br />
                        2. Click <strong>View Specialists</strong> to view all available specialists. Pick one (or
                        create your own) and click <strong>Select in Chat</strong> to enable it in your current chat.
                        <br />
                        3. Ask a question related to that specialist's expertise.
                      </div>
                      <div className="text-sm text-content-subtle">
                        If you don't want to use a specialist right now, click the button below to continue.
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => {
                            try {
                              localStorage.setItem('innex-chat-specialist-used', 'true');
                            } catch {}
                            setSteps((s) => ({ ...s, specialist: 'complete' }));
                          }}
                        >
                          I used a Specialist
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            try {
                              localStorage.setItem('innex-chat-specialist-used', 'true');
                            } catch {}
                            setSteps((s) => ({ ...s, specialist: 'complete' }));
                          }}
                        >
                          Skip for now
                        </Button>
                      </div>
                    </div>
                  ) : undefined}
                </Step>
              )}

              {allComplete && (
                <Step
                  title="Congrats, you're all set!"
                  state="loading"
                  isLast={true}
                  icon={<ConfettiIcon size={28} className="text-secondary-blue" />}
                >
                  <div className="text-sm text-content-subtle">
                    You're one step closer to getting the most out of CompliMind and saving yourself hours at work!
                  </div>
                </Step>
              )}
            </>
          );
        })()}
      </div>
    </RightPanelShell>
  );
}
